/**
 * ApprovedLinkBadge - Floating unlink control on approved link lines
 *
 * Features:
 * - Positioned at midpoint of connection line
 * - Shows "Linked" status with checkmark
 * - Unlink (X) button to remove the link
 * - Only visible on hover
 */

import { motion } from 'framer-motion';
import { Check, Loader2, Unlink } from 'lucide-react';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';

import { useAsyncAction } from '../hooks/useAsyncAction';
import { useBadgePosition } from '../hooks/useBadgePosition';
import type { Point } from '../types/linking-view.types';

interface ApprovedLinkBadgeProps {
  linkId: string;
  position: Point;
  onUnlink: () => void;
}

export const ApprovedLinkBadge: React.FC<ApprovedLinkBadgeProps> = ({
  linkId,
  position,
  onUnlink,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { ref, style } = useBadgePosition(position);
  const unlinkAction = useAsyncAction(onUnlink);

  return (
    <motion.div
      ref={ref}
      className="pointer-events-auto absolute z-30"
      style={style}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      data-link-id={linkId}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Linked badge - hidden on hover */}
      {!isHovered && (
        <motion.div
          className={cn(
            'flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs font-semibold shadow-md',
            'border border-emerald-200 text-emerald-600',
          )}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
        >
          <Check className="h-3 w-3" />
        </motion.div>
      )}

      {/* Unlink button - shown on hover, replaces the badge */}
      {isHovered && (
        <motion.button
          onClick={unlinkAction.execute}
          disabled={unlinkAction.isLoading}
          className={cn(
            'flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md transition-colors',
            'text-zinc-500 hover:bg-red-100 hover:text-red-600',
            unlinkAction.isLoading && 'cursor-not-allowed opacity-50',
          )}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          title="Remove link"
        >
          {unlinkAction.isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Unlink className="h-3.5 w-3.5" />
          )}
        </motion.button>
      )}
    </motion.div>
  );
};

export default ApprovedLinkBadge;
